import React, { useEffect, useState, useMemo } from "react";
import { useForm, Controller, useWatch } from "react-hook-form";
import DataTable from "react-data-table-component";
import { customTableStyles } from "../../assets/CustomTableStyles";
import { get } from "../../services/api";
import { toast } from "react-toastify";

// Utility function to format date as DD-MM-YYYY
const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

// Utility function to format date as DD-MMM
const formatShortDate = (dateStr) => {
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, "0");
  const month = d.toLocaleString("default", { month: "short" });
  return `${day}-${month}`;
};

function IndividualAttendanceModal({ onClose, student }) {
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const today = new Date().toISOString().split("T")[0];
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
  const minDate = oneYearAgo.toISOString().split("T")[0];

  const {
    register,
    formState: { errors },
    control,
    watch,
    trigger,
    setValue,
  } = useForm({
    defaultValues: {
      classId: null,
      classSectionId: null,
      studentId: student?.studentId || null,
      fromDate: today,
      toDate: today,
    },
  });

  const fromDate = useWatch({ control, name: "fromDate" });
  const toDate = useWatch({ control, name: "toDate" });
  const centerId =
    localStorage.getItem("centerId") === "null"
      ? null
      : localStorage.getItem("centerId");
  // Fetch report data
  const fetchReport = async (data) => {
    if (!data.fromDate || !data.toDate) return;
    if (errors.fromDate || errors.toDate) return;

    setLoading(true);
    try {
      const endpoint = `CenterAttendence/GetAttendanceReport?ClassSectionIds=${student.sectionId}&StudentIds=${student.studentId}&FromDate=${data.fromDate}&ToDate=${data.toDate}&CenterId=${centerId}`;
      const response = await get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.length) {
        setReportData(response || []);
      } else {
        setReportData([]);
        // toast.info("No attendance data found for the selected dates.");
      }
    } catch (error) {
      console.error("Error fetching report:", error);
      setReportData([]);
      toast.error("An error occurred while fetching the report.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on modal open and date changes
  useEffect(() => {
    trigger(["fromDate", "toDate"]).then((isValid) => {
      if (isValid) {
        fetchReport({ fromDate, toDate });
      }
    });
  }, [fromDate, toDate, trigger, student.sectionId, student.userId]);

  // Transform data for table
  const transformedData = useMemo(() => {
    if (!reportData.length || !reportData[0]?.attendanceReportDetails)
      return [];
    return reportData[0].attendanceReportDetails.map((detail, index) => ({
      id: index,
      date: detail.dayDate,
      status: detail.attnType || "-",
    }));
  }, [reportData]);

  // Define table columns
  const columns = useMemo(
    () => [
      {
        name: "Sr No.",
        cell: (row, index) => (currentPage - 1) * rowsPerPage + index + 1,
        sortable: false,
        width: "80px",
      },
      {
        name: "Date",
        selector: (row) => row.date,
        cell: (row) => formatDate(row.date),
        sortable: true,
        // width: "120px",
      },
      {
        name: "Status",
        selector: (row) => row.status,
        cell: (row) => (
          <span
            className={`mark-box ${
              row.status === "P"
                ? "present"
                : row.status === "L"
                ? "late-unauthorized"
                : row.status === "I"
                ? "late-authorized"
                : row.status === "S"
                ? "absent-approved"
                : row.status === "T"
                ? "absent-travel"
                : row.status === "M"
                ? "absent-medical"
                : row.status === "O"
                ? "absent-not-authorized"
                : row.status === "A"
                ? "absent-authorized"
                : row.status === "B"
                ? "early-dismissal-unauthorized"
                : row.status === "U"
                ? "early-dismissal-authorized"
                : row.status === "E"
                ? "early-dismissal-unauthorized"
                : row.status === "D"
                ? "early-dismissal-authorized"
                : ""
            }`}
          >
            {row.status}
          </span>
        ),
        sortable: true,
        // width: "100px",
      },
    ],
    [currentPage, rowsPerPage]
  );

  return (
    <>
      <div
        className="modal fade show"
        tabIndex="-1"
        style={{ display: "block" }}
      >
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="mb-0">
                Attendance Report{" "}
                <span className="text-info">{student.name}</span>
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                disabled={loading}
              ></button>
            </div>
            <div className="modal-body">
              <div className="row g-2 new-form-style">
                <div className="col-md-6">
                  <label className="form-label form-label-new">
                    From Date<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register("fromDate", {
                      required: "This field is required.",
                      validate: {
                        notFuture: (value) =>
                          value <= today ||
                          "From date cannot be in the future.",
                        withinRange: (value) =>
                          value >= minDate ||
                          "From date must be within the last year.",
                        beforeToDate: (value) =>
                          !toDate ||
                          value <= toDate ||
                          "From date must be before or equal to To date.",
                      },
                    })}
                    type="date"
                    className="form-control"
                    max={today}
                    min={minDate}
                    onChange={(e) => {
                      setValue("fromDate", e.target.value);
                      trigger("fromDate");
                    }}
                  />
                  {errors.fromDate && (
                    <small className="text-danger">
                      {errors.fromDate.message}
                    </small>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label form-label-new">
                    To Date<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register("toDate", {
                      required: "This field is required.",
                      validate: {
                        notFuture: (value) =>
                          value <= today || "To date cannot be in the future.",
                        withinRange: (value) =>
                          value >= minDate ||
                          "To date must be within the last year.",
                        afterFromDate: (value) =>
                          !fromDate ||
                          value >= fromDate ||
                          "To date must be after or equal to From date.",
                      },
                    })}
                    type="date"
                    className="form-control"
                    max={today}
                    min={fromDate || minDate}
                    onChange={(e) => {
                      setValue("toDate", e.target.value);
                      trigger("toDate");
                    }}
                  />
                  {errors.toDate && (
                    <small className="text-danger">
                      {errors.toDate.message}
                    </small>
                  )}
                </div>
              </div>

              {loading ? (
                <div className="text-center mt-3">
                  <span
                    className="spinner-border spinner-custom-size"
                    aria-hidden="true"
                  ></span>
                </div>
              ) : transformedData.length > 0 ? (
                <div className="card mt-3">
                  <div className="card-body py-1 px-1">
                    <div className="table-container att-scrollable-area-individual">
                      <DataTable
                        columns={columns}
                        data={transformedData}
                        customStyles={customTableStyles}
                        pagination
                        paginationRowsPerPageOptions={[
                          10, 25, 50, 100, 200, 300, 400, 500,
                        ]}
                        paginationDefaultPage={1}
                        paginationPerPage={rowsPerPage}
                        dense
                        onChangePage={setCurrentPage}
                        onChangeRowsPerPage={setRowsPerPage}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="card mt-3">
                  <div className="card-body text-center">
                    There are no records to display
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
}

export default IndividualAttendanceModal;
